import {
  AlertTriangle,
  BadgeRussianRuble,
  BookCheck,
  AppWindow,
  Lightbulb,
  Boxes,
  Box,
  Calculator,
  Camera,
  ClipboardList,
  Database,
  DoorClosed,
  DoorOpen,
  Download,
  Eye,
  FileClock,
  FileSpreadsheet,
  FileText,
  FileUp,
  Globe,
  Grid2X2,
  HelpCircle,
  Keyboard,
  History,
  Layers,
  Library,
  Link,
  ListTree,
  LogOut,
  Menu,
  MessageSquare,
  MessagesSquare,
  Moon,
  Move3D,
  PanelRightOpen,
  PencilRuler,
  Redo2,
  Ruler,
  PackageCheck,
  Scissors,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Sun,
  Table2,
  Undo2,
  Upload,
  Variable,
  Wrench,
  Zap as IconZap,
} from 'lucide-react';
import {
  IconCatalog,
  IconConstructive,
  IconConstructor,
  IconDrawers,
  IconFacades,
  IconFill,
  IconHardware,
  IconLayout,
  IconMaterials,
  IconModels,
  IconOrders,
  IconStructure,
  IconTemplates,
  IconUpload,
} from '@/shared/icons/rail-icons';

import { groupOfCommand } from './command-groups';
import type {
  CommandDestination,
  SurfaceDescriptor,
  UiCommand,
  WorkbenchModule,
  WorkbenchModuleId,
  WorkbenchPlacement,
  WorkbenchCapability,
} from './types';

/** Роды поверхностей, которые ПЕРЕВОДЯТ человека в другое место продукта. */
const NAVIGABLE_KINDS = new Set<SurfaceDescriptor['kind']>([
  'workspace', 'sceneMode', 'leftRail', 'rightRail', 'techSection',
]);

function surface(
  moduleId: WorkbenchModuleId,
  kind: SurfaceDescriptor['kind'],
  key: SurfaceDescriptor['key'],
  label: string,
  hint: string,
  icon: SurfaceDescriptor['icon'],
  placements: readonly WorkbenchPlacement[],
  destination: CommandDestination,
  options: Partial<Pick<UiCommand, 'hotkeys' | 'requiresActiveModel' | 'searchTerms'>> = {},
): { descriptor: SurfaceDescriptor; command: UiCommand } {
  const commandId = `${kind}.${String(key)}`;
  /* Глагол берётся из одной таблицы (`command-groups.ts`), а не вписывается
     рядом с каждой командой: именно вписывание набрало 18 групп — каждый новый
     экран приносил свою. Неизвестный идентификатор получает НАЗВАННЫЙ заголовок
     «Без группы», а не тихое умолчание. Пин: registry-groups.test.ts. */
  const group = groupOfCommand(commandId);
  // НАВИГАЦИЯ ОБЯЗАНА ИСКАТЬСЯ.
  //
  // Замер реестра: из 39 навигационных поверхностей (рабочие места, режимы
  // сцены, левые и правые рейки, разделы АРМ Технолога) в палитре не было
  // НИ ОДНОЙ — ⌘K знал только «способности» и старые псевдонимы. Набрав
  // «раскрой» или «чертежи», человек не находил ничего.
  //
  // Правило пишется здесь, в помощнике, а не в 39 местах вызова: строка,
  // которую забыли дописать, — это ровно тот способ, каким половина продукта
  // и выпала из поиска. Пин: palette-reach.test.ts.
  const commandPlacements = NAVIGABLE_KINDS.has(kind) && !placements.includes('command-palette')
    ? ([...placements, 'command-palette'] as readonly WorkbenchPlacement[])
    : placements;
  return {
    descriptor: {
      id: commandId,
      commandId,
      moduleId,
      kind,
      key,
      label,
      hint,
      group,
      icon,
      placements,
      rendererId: kind === 'leftRail' || kind === 'rightRail' ? `rail:${String(key)}` : undefined,
    },
    command: {
      id: commandId,
      moduleId,
      label,
      hint,
      group,
      icon,
      destination,
      placements: commandPlacements,
      ...options,
    },
  };
}

const workspaceSpecs = [
  surface('viewer-scene', 'workspace', 'scene', 'Сцена', '3D-вьюпорт — дизайн и компоновка', Box, ['all-tools'], { kind: 'workspace', value: 'scene' }),
  surface('technologist', 'workspace', 'tech', 'Технолог', 'Материалы, операции, смета, раскрой, брифы и ревизии', Settings2, ['all-tools'], { kind: 'workspace', value: 'tech' }),
  surface('orders', 'workspace', 'orders', 'Заказы', 'CRM — менеджеры, клиенты и договоры', ClipboardList, ['all-tools'], { kind: 'workspace', value: 'orders' }),
  /* A3: лобби реестра моделей с состояниями пайплайна — декод → ревью →
   * опубликовано → карантин (справочник каталога отдельной вкладкой).
   * Состояние считает сервер (model_registry.model_pipeline_state). */
  surface('project-app', 'workspace', 'models', 'Реестр моделей', 'Лобби пайплайна: декод → ревью → публикация → карантин', Boxes, ['all-tools'], { kind: 'workspace', value: 'models' }),
] as const;

const sceneModeSpecs = [
  surface('viewer-scene', 'sceneMode', 'scene', 'Сцена', 'Компоновка → 3D', Box, ['all-tools'], { kind: 'scene-mode', value: 'scene' }),
  surface('viewer-scene', 'sceneMode', 'plan', 'План', 'Компоновка → План', Grid2X2, ['all-tools'], { kind: 'scene-mode', value: 'plan' }),
  surface('viewer-scene', 'sceneMode', 'modules', 'Модули', 'Конструкция → Модули', Table2, ['all-tools'], { kind: 'scene-mode', value: 'modules' }),
  surface('viewer-scene', 'sceneMode', 'params', 'Переменные', 'Конструкция → Переменные', Variable, ['all-tools'], { kind: 'scene-mode', value: 'params' }),
  surface('viewer-scene', 'sceneMode', 'analysis', 'Анализ', 'Проверка → Анализ', AlertTriangle, ['all-tools'], { kind: 'scene-mode', value: 'analysis' }),
  surface('viewer-scene', 'sceneMode', 'verify', 'По источнику', 'Проверка → По источнику', ShieldCheck, ['all-tools'], { kind: 'scene-mode', value: 'verify' }),
  surface('viewer-scene', 'sceneMode', 'estimate', 'Смета', 'Полный экран сметы', Calculator, ['all-tools'], { kind: 'scene-mode', value: 'estimate' }),
  surface('viewer-scene', 'sceneMode', 'drawings', 'Чертежи', 'Производство → Чертежи', PencilRuler, ['all-tools'], { kind: 'scene-mode', value: 'drawings' }),
] as const;

const leftSpecs = [
  surface('project-app', 'leftRail', 'electrical', 'Электрика', 'Розетки, вода и свет на стенах', IconZap, ['all-tools'], { kind: 'left-rail', value: 'electrical' }),
  surface('project-app', 'leftRail', 'structure', 'Структура', 'Дерево сцены и проекта', IconStructure, ['all-tools'], { kind: 'left-rail', value: 'structure' }),
  surface('project-app', 'leftRail', 'models', 'Модели', 'Выбор и поиск модели', IconModels, ['all-tools'], { kind: 'left-rail', value: 'models' }),
  surface('viewer-scene', 'leftRail', 'catalog', 'Каталог', 'Палитра параметрических изделий', IconCatalog, ['all-tools'], { kind: 'left-rail', value: 'catalog' }),
  surface('viewer-scene', 'leftRail', 'cabinetTemplates', 'Шаблоны', 'Шаблоны шкафов', IconTemplates, ['all-tools'], { kind: 'left-rail', value: 'cabinetTemplates' }),
  /*
   * НАПОЛНЕНИЕ ВЕРНУЛОСЬ В ЛЕНТУ — ОДНОЙ ПАНЕЛЬЮ И НА ДРУГОМ ЯЗЫКЕ.
   *
   * Четыре прежних канала спрашивали одно и то же четырьмя формами. Здесь спрашивается то, чем
   * человек думает: ВОТ ЭТА НИША и ВОТ ЭТА ВЕЩЬ в неё. Ателье остаётся местом, где модуль
   * правят целиком; лента — местом, где кладут вещь в нишу, не открывая окна во весь экран.
   */
  surface('viewer-scene', 'leftRail', 'fill', 'Наполнение', 'Ниши выбранного модуля: полки, ящики, створки', IconFill, ['all-tools'], { kind: 'left-rail', value: 'fill' }),
  /*
   * ЧЕТЫРЕ КАНАЛА ПРАВКИ МОДУЛЯ УБРАНЫ ИЗ ЛЕНТЫ: «Фасады», «Ящики»,
   * «Конструктив», «Наполнение».
   *
   * Все четыре правили ту же команду наполнения (`ModuleFillOperationPayload`),
   * что и ателье, — то есть были вторым входом в одно действие. Расходятся такие
   * входы молча: человек ставит полку в панели, открывает ателье и видит там
   * прежний корпус, потому что окно читало модель до правки.
   *
   * Правка модуля живёт в ателье: оно открывается из карточки модуля и
   * показывает шкаф целиком — фасады, ящики, ниши, наполнение — на его
   * собственных числах.
   */
  surface('viewer-scene', 'leftRail', 'layoutTools', 'Расстановка', 'Инструменты компоновки', IconLayout, ['all-tools'], { kind: 'left-rail', value: 'layoutTools' }),
  surface('viewer-scene', 'leftRail', 'kitchenConstructor', 'Конструктор', 'Параметрический конструктор изделий', IconConstructor, ['all-tools'], { kind: 'left-rail', value: 'kitchenConstructor' }),
  surface('orders', 'leftRail', 'orders', 'Заказы', 'Быстрый доступ к заказам', IconOrders, ['all-tools'], { kind: 'left-rail', value: 'orders' }),
  surface('technologist', 'leftRail', 'materials', 'Материалы', 'Каталог материалов', IconMaterials, ['all-tools'], { kind: 'left-rail', value: 'materials' }),
  surface('technologist', 'leftRail', 'hardware', 'Фурнитура', 'Каталог фурнитуры', IconHardware, ['all-tools'], { kind: 'left-rail', value: 'hardware' }),
  surface('project-app', 'leftRail', 'ingest', 'Загрузка', 'Импорт B3D, PDF и GLB', IconUpload, ['all-tools'], { kind: 'left-rail', value: 'ingest' }),
] as const;

const rightSpecs = [
  surface('viewer-scene', 'rightRail', 'properties', 'Свойства', 'Контекст выбранного объекта', PanelRightOpen, ['all-tools'], { kind: 'right-rail', value: 'properties' }),
  surface('viewer-scene', 'rightRail', 'furnitureList', 'Список мебели', 'Состав мебели проекта', Boxes, ['all-tools'], { kind: 'right-rail', value: 'furnitureList' }),
  surface('viewer-scene', 'rightRail', 'estimate', 'Смета', 'Быстрая смета', Calculator, ['all-tools'], { kind: 'right-rail', value: 'estimate' }),
  surface('viewer-scene', 'rightRail', 'analysis', 'Анализ', 'Проверки сцены', AlertTriangle, ['all-tools'], { kind: 'right-rail', value: 'analysis' }),
  surface('viewer-scene', 'rightRail', 'knowledge', 'База знаний', 'Регламенты и знания', Database, ['all-tools'], { kind: 'right-rail', value: 'knowledge' }),
  surface('viewer-scene', 'rightRail', 'annotations', 'Замечания', 'Замечания по модели', MessageSquare, ['all-tools'], { kind: 'right-rail', value: 'annotations' }),
  surface('viewer-scene', 'rightRail', 'chat', 'Чат модели', 'История обсуждения модели', MessagesSquare, ['all-tools'], { kind: 'right-rail', value: 'chat' }),
  surface('viewer-scene', 'rightRail', 'renders', 'Рендеры', 'Рендеры и снимки', Camera, ['all-tools'], { kind: 'right-rail', value: 'renders' }),
  surface('project-app', 'rightRail', 'history', 'История', 'Версии проекта', History, ['all-tools'], { kind: 'right-rail', value: 'history' }),
] as const;

const techSpecs = [
  surface('technologist', 'techSection', 'materials', 'Материалы', 'Каталог материалов и фурнитуры', Library, ['all-tools'], { kind: 'tech-section', value: 'materials' }),
  surface('technologist', 'techSection', 'operations', 'Операции', 'Операции и сопутствие', Wrench, ['all-tools'], { kind: 'tech-section', value: 'operations' }),
  surface('technologist', 'techSection', 'smeta', 'Смета', 'Полная смета технолога', FileSpreadsheet, ['all-tools'], { kind: 'tech-section', value: 'smeta' }),
  surface('technologist', 'techSection', 'cut', 'Раскрой', 'Раскрой, nesting, контуры и CNC', Scissors, ['all-tools'], { kind: 'tech-section', value: 'cut' }),
  surface('technologist', 'techSection', 'production', 'Производство', 'Сводка фурнитуры, пакет закупки и лист присадки', PackageCheck, ['all-tools'], { kind: 'tech-section', value: 'production' }),
  surface('technologist', 'techSection', 'advisor', 'Советы', 'Подсказки технолога с применением в один клик', Lightbulb, ['all-tools'], { kind: 'tech-section', value: 'advisor' }),
  surface('technologist', 'techSection', 'review', 'Ревью', 'Таблица оператора: деталь → роль → номинал → источник', ClipboardList, ['all-tools'], { kind: 'tech-section', value: 'review' }),
  surface('technologist', 'techSection', 'briefs', 'Брифы', 'Клиентские брифы', FileText, ['all-tools'], { kind: 'tech-section', value: 'briefs' }),
  surface('technologist', 'techSection', 'pricebook', 'Прайс-книга', 'Цены как процесс: черновик → CSV-проверка → утверждение', BadgeRussianRuble, ['all-tools'], { kind: 'tech-section', value: 'pricebook' }),
  surface('technologist', 'techSection', 'library-review', 'Обзор библиотеки', 'Ревью-конвейер каталога: очередь готовности и пакет привязок', BookCheck, ['all-tools'], { kind: 'tech-section', value: 'library-review' }),
  surface('technologist', 'techSection', 'revisions', 'Ревизии', 'Ревизии материалов и смет', FileClock, ['all-tools'], { kind: 'tech-section', value: 'revisions' }),
] as const;

function capabilitySurface(
  moduleId: WorkbenchModuleId,
  commandId: string,
  capability: WorkbenchCapability,
  label: string,
  hint: string,
  icon: SurfaceDescriptor['icon'],
  options: Partial<Pick<UiCommand, 'hotkeys' | 'requiresActiveModel' | 'searchTerms'>> = {},
  placements: readonly WorkbenchPlacement[] = ['all-tools', 'command-palette'],
) {
  const item = surface(moduleId, 'projectAction', capability, label, hint, icon, placements, { kind: 'capability', value: capability }, options);
  item.descriptor.id = commandId;
  item.descriptor.commandId = commandId;
  item.command.id = commandId;
  /* Идентификатор здесь ПЕРЕПИСЫВАЕТСЯ (`projectAction.open-recognition` →
     `app.recognition`), поэтому глагол обязан пересчитаться по новому: иначе
     таблица искалась бы по имени, которого в продукте нет, и вся эта ветвь
     реестра молча уехала бы в «Без группы». */
  item.descriptor.group = groupOfCommand(commandId);
  item.command.group = item.descriptor.group;
  return item;
}

const projectActionSpecs = [
  capabilitySurface('project-app', 'app.buildings', 'open-buildings', 'Наши постройки', 'Проекты павильонов и ведомости материалов', Box),
  capabilitySurface('project-app', 'app.event-location', 'open-event-location', 'Локации для ивентов', 'Конструктор локаций, примитивы и варианты', Camera),
  capabilitySurface('project-app', 'app.recognition', 'open-recognition', 'Распознавание', 'Открыть АРМ распознавания', Sparkles),
  capabilitySurface('project-app', 'app.mesh-studio', 'open-mesh-studio', 'Mesh Studio', 'PRO100 / SKP decoder workspace', Boxes),
  capabilitySurface('project-app', 'project.model-picker', 'open-model-picker', 'Выбрать модель', 'Поиск и выбор модели проекта', Search),
  capabilitySurface('project-app', 'project.menu', 'open-project-menu', 'Меню проекта', 'Импорт, экспорт и публикация', Menu),
  capabilitySurface('project-app', 'project.share', 'share-project', 'Поделиться', 'Создать публичную ссылку', Link, { requiresActiveModel: true }),
  capabilitySurface('project-app', 'project.export', 'export-project', 'Экспорт .holzcom', 'Скачать проект', Download, { requiresActiveModel: true }),
  capabilitySurface('project-app', 'project.import', 'import-project', 'Импорт .holzcom', 'Загрузить проект', Upload),
  capabilitySurface('project-app', 'project.import-source', 'open-scene-pipeline', 'Импорт изображений / PDF / B3D / GLB', 'Открыть конвейер загрузки кухни или исходной модели', FileUp),
  capabilitySurface('viewer-scene', 'image-kitchen.upload', 'open-image-kitchen-upload', 'Кухня по изображениям', 'Загрузить фото в отдельный image pipeline', FileUp, {}, ['all-tools', 'command-palette']),
  capabilitySurface('viewer-scene', 'image-kitchen.graph1', 'open-image-kitchen-graph1', 'Graph1 · распознавание', 'Открыть сохранённый Graph1 и его lineage', Sparkles, {}, ['all-tools', 'command-palette']),
  capabilitySurface('viewer-scene', 'image-kitchen.graph2', 'open-image-kitchen-graph2', 'Graph2 · сопоставление', 'Сопоставить Graph1 с точными позициями каталога', ShieldCheck, {}, ['all-tools', 'command-palette']),
  capabilitySurface('viewer-scene', 'image-kitchen.model', 'open-image-kitchen-model', 'Модель кухни', 'Опубликовать или открыть построенную canonical model', Box, {}, ['all-tools', 'command-palette']),
  capabilitySurface('viewer-scene', 'image-kitchen.smeta', 'open-image-kitchen-smeta', 'Смета кухни', 'Создать, импортировать и перечитать каноническую смету', Calculator, {}, ['all-tools', 'command-palette']),
  capabilitySurface('project-app', 'app.locale', 'toggle-locale', 'Язык RU / EN', 'Переключить язык', Globe),
  capabilitySurface('project-app', 'app.appearance', 'toggle-appearance', 'Тема', 'Светлая или тёмная тема', Moon, { hotkeys: ['Mod+T'] }),
  capabilitySurface('project-app', 'app.tour', 'start-tour', 'Обучение', 'Открыть обучение', HelpCircle),
  capabilitySurface('project-app', 'app.command-palette', 'open-command-palette', 'Команды', 'Глобальная командная палитра', Search, { hotkeys: ['Mod+K'] }),
  /* Экран сокращений (§7.11 п.6). Сочетание объявлено ЗДЕСЬ, иначе палитра о
     нём не напечатает, и экран про клавиатуру сам оказался бы клавишей,
     о которой нельзя узнать. */
  capabilitySurface('project-app', 'app.shortcuts', 'open-shortcuts', 'Сокращения', 'Все сочетания клавиш на одном экране', Keyboard, { hotkeys: ['?'], searchTerms: ['клавиши', 'горячие', 'hotkeys', 'shortcuts'] }),
  capabilitySurface('project-app', 'history.undo', 'undo', 'Отменить', 'Отменить последнюю операцию', Undo2, { hotkeys: ['Mod+Z'], requiresActiveModel: true }),
  capabilitySurface('project-app', 'history.redo', 'redo', 'Вернуть', 'Вернуть отменённую операцию', Redo2, { hotkeys: ['Mod+Shift+Z'], requiresActiveModel: true }),
  capabilitySurface('project-app', 'project.diagnostics', 'project-diagnostics', 'Статус проекта', 'Объекты, BOM, габариты и синхронизация', ShieldCheck),
  capabilitySurface('project-app', 'app.logout', 'logout', 'Выйти', 'Завершить сессию', LogOut),
] as const;

const viewportSpecs = [
  capabilitySurface('viewer-scene', 'viewport.projection', 'viewport-controls', 'Проекция и виды', 'Perspective, ISO, Top, Front, Side', Camera, { searchTerms: ['камера', 'projection'] }, ['all-tools']),
  capabilitySurface('viewer-scene', 'viewport.layers', 'viewport-layers', 'Слои и стены', 'Furniture, apartment и wall visibility', Layers, { searchTerms: ['body', 'details', 'walls'] }, ['all-tools']),
  capabilitySurface('viewer-scene', 'viewport.dimensions', 'viewport-dimensions', 'Размеры и стиль', 'Scope и Client / Production / Install', Ruler, {}, ['all-tools']),
  capabilitySurface('viewer-scene', 'viewport.environment', 'viewport-environment', 'Свет, HDRI и эффекты', 'Световые пресеты и визуальные эффекты', Sun, {}, ['all-tools']),
  capabilitySurface('viewer-scene', 'module.actions', 'module-actions', 'Действия модуля', 'Transform, placement, constraints и state', Move3D, {}, ['all-tools']),
] as const;

function viewerCommand(
  id: string,
  label: string,
  icon: UiCommand['icon'],
  destination: CommandDestination,
  hotkeys?: readonly string[],
): UiCommand {
  return {
    id,
    moduleId: destination.kind === 'left-rail' || destination.kind === 'right-rail' || destination.kind === 'viewer-action'
      ? 'viewer-scene'
      : 'project-app',
    label,
    hint: label,
    group: groupOfCommand(id),
    icon,
    destination,
    // Viewer actions stay visible in both global discovery surfaces.
    // They execute through the same runtime destinations and one command owner.
    placements: ['command-palette', 'all-tools'],
    hotkeys,
  };
}

const viewerCommands: readonly UiCommand[] = [
  viewerCommand('view-persp', 'Перспектива', Camera, { kind: 'viewer-action', value: 'projection:perspective' }, ['V P']),
  viewerCommand('view-iso', 'Изометрия', Camera, { kind: 'viewer-action', value: 'projection:iso' }, ['V I']),
  viewerCommand('view-top', 'Сверху', Camera, { kind: 'viewer-action', value: 'projection:top' }, ['V T']),
  viewerCommand('view-front', 'Спереди', Camera, { kind: 'viewer-action', value: 'projection:front' }, ['V F']),
  viewerCommand('view-side', 'Сбоку', Camera, { kind: 'viewer-action', value: 'projection:side' }, ['V S']),
  viewerCommand('mode-solid', 'Solid', Box, { kind: 'viewer-action', value: 'render:solid' }, ['1']),
  viewerCommand('mode-wire', 'Wireframe', Box, { kind: 'viewer-action', value: 'render:wireframe' }, ['2']),
  viewerCommand('mode-xray', 'X-Ray', Eye, { kind: 'viewer-action', value: 'render:xray' }, ['3']),
  viewerCommand('light-studio', 'Свет: Studio', Sun, { kind: 'viewer-action', value: 'light:studio' }),
  viewerCommand('light-show', 'Свет: Showroom', Sun, { kind: 'viewer-action', value: 'light:showroom' }),
  viewerCommand('light-day', 'Свет: Daylight', Sun, { kind: 'viewer-action', value: 'light:daylight' }),
  viewerCommand('light-photo', 'Свет: Photo', Sun, { kind: 'viewer-action', value: 'light:photo' }),
  viewerCommand('dim-off', 'Размеры выкл', Ruler, { kind: 'viewer-action', value: 'dimension:off' }),
  viewerCommand('dim-on', 'Показать размеры изделия', Ruler, { kind: 'viewer-action', value: 'dimension:overall' }),
  viewerCommand('dim-inline', 'Размеры внутри ниш', Ruler, { kind: 'viewer-action', value: 'dimension:inline' }),
  viewerCommand('explode-on', 'Взрыв-схема', Layers, { kind: 'viewer-action', value: 'explode:on' }),
  viewerCommand('explode-off', 'Собрать обратно', Layers, { kind: 'viewer-action', value: 'explode:off' }),
  viewerCommand('section', 'Разрез по Z', Scissors, { kind: 'viewer-action', value: 'section:z' }),
  viewerCommand('render', 'Запустить Final Photo', Sparkles, { kind: 'right-rail', value: 'renders' }),
  viewerCommand('panel-struct', 'Открыть Структуру', ListTree, { kind: 'left-rail', value: 'structure' }),
  viewerCommand('panel-fill', 'Открыть Наполнение', Grid2X2, { kind: 'left-rail', value: 'fill' }),
  viewerCommand('panel-mat', 'Открыть Материалы', Library, { kind: 'left-rail', value: 'materials' }),
  viewerCommand('panel-est', 'Открыть Смету', Calculator, { kind: 'right-rail', value: 'estimate' }),
  viewerCommand('panel-anal', 'Открыть Анализ', AlertTriangle, { kind: 'right-rail', value: 'analysis' }),
  viewerCommand('panel-hist', 'Открыть Историю', History, { kind: 'right-rail', value: 'history' }),
  viewerCommand('panel-ing', 'Загрузить модель', FileUp, { kind: 'left-rail', value: 'ingest' }),
  viewerCommand('theme', 'Переключить тему', Moon, { kind: 'capability', value: 'toggle-appearance' }, ['Mod+T']),
  /*
   * СОКРАЩЕНИЯ, КОТОРЫЕ РАБОТАЛИ, НО НЕ БЫЛИ ОБЪЯВЛЕНЫ.
   *
   * Замер: живых сочетаний 19, объявлено 15. `I`, `O`, `Shift+O` и `Delete`
   * разбирал глобальный обработчик в `app/App.tsx`, и человек не мог узнать
   * о них ниоткуда: палитра печатает только `hotkeys[0]`. Ниже они
   * объявлены — поведение не менялось, менялась ВИДИМОСТЬ.
   *
   * `Delete` БОЛЬШЕ НЕ ОБЪЯВЛЕН сокращением к «Скрыть» — потому что больше им
   * и не является (§7.6 правило 2, §7.11 п.7). Клавиша разрушения, которая
   * прячет вместо того чтобы удалять, — ловушка: шкаф исчезает с экрана, и
   * человек уверен, что удалил его. Скрытие осталось на `H`; `Delete` теперь
   * объясняет словами, что он не делает, и куда идти за удалением.
   *
   * Пин: keyboard-declared.test.ts.
   */
  viewerCommand('selection.isolate', 'Изолировать выбранное', Eye, { kind: 'viewer-action', value: 'selection:isolate' }, ['I']),
  viewerCommand('selection.hide', 'Скрыть выбранное', Eye, { kind: 'viewer-action', value: 'selection:hide' }, ['H']),
  viewerCommand('selection.show-all', 'Показать всё', Eye, { kind: 'viewer-action', value: 'selection:show-all' }, ['Shift+H']),
  viewerCommand('panels.open-all', 'Открыть все фасады', DoorOpen, { kind: 'viewer-action', value: 'panels:open-all' }, ['O']),
  viewerCommand('panels.close-all', 'Закрыть все фасады', DoorClosed, { kind: 'viewer-action', value: 'panels:close-all' }, ['Shift+O']),
];

const allSpecs = [
  ...workspaceSpecs,
  ...sceneModeSpecs,
  ...leftSpecs,
  ...rightSpecs,
  ...techSpecs,
  ...projectActionSpecs,
  ...viewportSpecs,
];

function moduleFrom(id: WorkbenchModuleId, label: string, enabledByDefault = true): WorkbenchModule {
  const owned = allSpecs.filter((item) => item.command.moduleId === id);
  const commands = viewerCommands.filter((command) => command.moduleId === id);
  return {
    id,
    label,
    enabledByDefault,
    visualIdentity: 'owned-purple',
    capabilities: [],
    surfaces: owned.map((item) => item.descriptor),
    commands: [...owned.map((item) => item.command), ...commands],
  };
}

export const WORKBENCH_MODULES: readonly WorkbenchModule[] = [
  moduleFrom('project-app', 'Проект и приложение'),
  moduleFrom('viewer-scene', 'Viewer Scene'),
  moduleFrom('technologist', 'АРМ Технолог'),
  moduleFrom('orders', 'Заказы'),
  {
    id: 'mesh-studio',
    label: 'Mesh Studio · PRO100 / SKP',
    enabledByDefault: false,
    visualIdentity: 'inherits-shell',
    capabilities: ['decode:pro100', 'decode:skp'],
    surfaces: [],
    commands: [],
  },
] as const;

export const WORKBENCH_COMMANDS: readonly UiCommand[] = WORKBENCH_MODULES
  .filter((module) => module.enabledByDefault)
  .flatMap((module) => module.commands);

export const WORKSPACE_SURFACES = workspaceSpecs.map((item) => item.descriptor);
export const SCENE_MODE_SURFACES = sceneModeSpecs.map((item) => item.descriptor);
export const LEFT_RAIL_SURFACES = leftSpecs.map((item) => item.descriptor);
export const RIGHT_RAIL_SURFACES = rightSpecs.map((item) => item.descriptor);
export const TECH_SECTION_SURFACES = techSpecs.map((item) => item.descriptor);

export const WORKBENCH_COMMAND_BY_ID = new Map(WORKBENCH_COMMANDS.map((command) => [command.id, command]));

export function commandsForPlacement(placement: WorkbenchPlacement): UiCommand[] {
  return WORKBENCH_COMMANDS.filter((command) => command.placements.includes(placement));
}
